import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, fromEvent, switchMap } from 'rxjs';

@Component({
  selector: 'ntt-dropdown-menu',
  templateUrl: './dropdown-menu.html',
  styleUrl: './dropdown-menu.scss',
})
export class NttDropdownMenu {
  private readonly _destroyRef = inject(DestroyRef);

  readonly anchor = input<string>('');
  readonly trigger = input<HTMLElement | null>(null);
  readonly disabled = input(false);

  private readonly _popoverRef =
    viewChild<ElementRef<HTMLElement>>('popoverRef');
  private readonly _isOpen = signal(false);
  private readonly _dynamicAnchor = signal<string | null>(null);

  readonly isOpen = this._isOpen.asReadonly();

  readonly anchorName = computed(() => {
    const dynamicAnchor = this._dynamicAnchor();
    if (dynamicAnchor) {
      return `--${dynamicAnchor}`;
    }
    const anchor = this.anchor();
    if (anchor) {
      return `--${anchor}`;
    }
    return null;
  });

  constructor() {
    toObservable(this.trigger)
      .pipe(
        filter((trigger): trigger is HTMLElement => trigger !== null),
        switchMap((trigger) => fromEvent(trigger, 'click')),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => this.toggle());

    toObservable(this._popoverRef)
      .pipe(
        filter((ref): ref is ElementRef<HTMLElement> => ref !== undefined),
        switchMap((ref) => fromEvent(ref.nativeElement, 'toggle')),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((event: Event) => {
        const toggleEvent = event as ToggleEvent;
        this._isOpen.set(toggleEvent.newState === 'open');
      });
  }

  open(): void {
    if (this.disabled()) return;
    const popover = this._popoverRef()?.nativeElement;
    if (popover && 'showPopover' in popover) {
      popover.showPopover();
    }
  }

  close(): void {
    const popover = this._popoverRef()?.nativeElement;
    if (popover && 'hidePopover' in popover) {
      popover.hidePopover();
    }
  }

  toggle(): void {
    if (this.disabled()) return;
    const popover = this._popoverRef()?.nativeElement;
    if (popover && 'togglePopover' in popover) {
      popover.togglePopover();
    }
  }

  setAnchor(anchorId: string): void {
    this._dynamicAnchor.set(anchorId);
  }
}
